import { useState, useEffect, useCallback } from "react";
import apiClient from "../../services/apiClient";
import { dataLoaderUri, mobileAppUri } from "../../API/API";
import {
  OverviewData,
  EngagementData,
  LearningData,
  ContentData,
  NotificationData,
  TopUsersData,
  UserDetailData,
  AnalyticsApiResponse,
  AnalyticsFilters,
  LLMUsageData,
  LLMUserUsage,
  LLMModelUsage,
  LLMEndpointUsage,
  LLMDailyStat,
  DataQualityData,
  ExerciseUser,
} from "../types/analytics.types";
import { GrowthData, CostsData } from "../types/growth.types";

// Base analytics API URL
const ANALYTICS_BASE_URL = `${dataLoaderUri}/api/v1/analytics`;

// Cache management
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache: Record<string, CacheEntry<any>> = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Generic analytics fetch hook
function useAnalytics<T>(endpoint: string, filters: AnalyticsFilters, enabled: boolean = true) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Generate cache key from endpoint and filters
  const getCacheKey = useCallback(() => {
    return `${endpoint}_${JSON.stringify(filters)}`;
  }, [endpoint, filters]);

  // Check cache
  const checkCache = useCallback((): T | null => {
    const cacheKey = getCacheKey();
    const cached = cache[cacheKey];

    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      console.log(`Cache hit for ${endpoint}`);
      return cached.data;
    }

    return null;
  }, [getCacheKey, endpoint]);

  // Save to cache
  const saveToCache = useCallback(
    (responseData: T) => {
      const cacheKey = getCacheKey();
      cache[cacheKey] = {
        data: responseData,
        timestamp: Date.now(),
      };
    },
    [getCacheKey]
  );

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    // Check cache first
    const cachedData = checkCache();
    if (cachedData) {
      setData(cachedData);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params: Record<string, string> = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
      };

      if (filters.targetLanguage) params.target_language = filters.targetLanguage;
      if (filters.baseLanguage) params.base_language = filters.baseLanguage;
      if (filters.section) params.section = filters.section;
      if (filters.component) params.component = filters.component;
      if (filters.granularity) params.granularity = filters.granularity;
      if (filters.metricType) params.metric_type = filters.metricType;

      // Build URL with query params
      const queryString = new URLSearchParams(params).toString();
      const url = `${ANALYTICS_BASE_URL}/${endpoint}?${queryString}`;

      console.log(`Fetching analytics: ${url}`);

      // Fetch data
      const response: AnalyticsApiResponse<T> = await apiClient.get(url);

      const responseData = response.data;
      setData(responseData);
      saveToCache(responseData);
    } catch (err) {
      console.error(`Error fetching analytics ${endpoint}:`, err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [endpoint, filters, enabled, checkCache, saveToCache]);

  // Refetch function
  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Specific hooks for each analytics endpoint

export function useOverviewAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<OverviewData>("overview", filters);
}

export function useEngagementAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<EngagementData>("engagement", filters);
}

export function useLearningAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<LearningData>("learning", filters);
}

export function useContentAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<ContentData>("content", filters);
}

export function useGrowthAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<GrowthData>("growth", filters);
}

export function useCostsAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<CostsData>("costs", filters);
}

export function useDataQualityAnalytics(
  filters: AnalyticsFilters,
  minAttempts: number = 5,
  failureRateThreshold: number = 70,
  reviewStatus: string = "",
  includeSkips: boolean = true
) {
  const [data, setData] = useState<DataQualityData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
        min_attempts: minAttempts.toString(),
        failure_rate_threshold: failureRateThreshold.toString(),
        include_skips: includeSkips ? "true" : "false",
      };

      if (filters.targetLanguage) params.target_language = filters.targetLanguage;
      if (reviewStatus) params.review_status = reviewStatus;

      const queryString = new URLSearchParams(params).toString();
      const url = `${ANALYTICS_BASE_URL}/data-quality?${queryString}`;

      console.log(`Fetching data quality analytics: ${url}`);

      const response: AnalyticsApiResponse<DataQualityData> = await apiClient.get(url);
      setData(response.data);
    } catch (err) {
      console.error("Error fetching data quality analytics:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters, minAttempts, failureRateThreshold, reviewStatus, includeSkips]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  // Manual only - no auto-fetch on mount or filter change
  // User must press "Scan" button

  const setDataDirectly = setData;

  return { data, loading, error, refetch, setData: setDataDirectly };
}

export async function fetchExerciseUsers(
  exerciseKey: string,
  startDate: Date,
  endDate: Date,
  includeSkips: boolean = true
): Promise<ExerciseUser[]> {
  const params: Record<string, string> = {
    action: "get_exercise_users",
    exercise_key: exerciseKey,
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
    include_skips: includeSkips ? "true" : "false",
  };

  const queryString = new URLSearchParams(params).toString();
  const url = `${ANALYTICS_BASE_URL}/data-quality?${queryString}`;

  const response = await apiClient.get(url);
  // Response may be wrapped in AnalyticsApiResponse or direct
  const data = response.data || response;
  return data.users || [];
}

export function useNotificationAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<NotificationData>("notifications", filters);
}

export function useTopUsersAnalytics(filters: AnalyticsFilters, limit: number = 50) {
  const [data, setData] = useState<TopUsersData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
        limit: limit.toString(),
        // No mode parameter - defaults to 'login' for efficient queries
      };

      if (filters.targetLanguage) params.target_language = filters.targetLanguage;

      const queryString = new URLSearchParams(params).toString();
      const url = `${ANALYTICS_BASE_URL}/users?${queryString}`;

      console.log(`Fetching top users: ${url}`);

      const response: AnalyticsApiResponse<TopUsersData> = await apiClient.get(url);
      setData(response.data);
    } catch (err) {
      console.error("Error fetching top users:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters, limit]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Daily session stats types
export interface DailySessionStat {
  date: string;
  session_count: number;
  unique_users: number;
  total_duration_minutes: number;
  avg_duration_minutes: number;
}

export interface DailySessionData {
  daily_stats: DailySessionStat[];
  totals: {
    total_sessions: number;
    total_unique_users: number;
    total_duration_minutes: number;
    avg_duration_minutes: number;
  };
}

export function useDailySessionAnalytics(filters: AnalyticsFilters) {
  const [data, setData] = useState<DailySessionData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
        mode: "daily",
      };

      if (filters.targetLanguage) params.target_language = filters.targetLanguage;

      const queryString = new URLSearchParams(params).toString();
      const url = `${ANALYTICS_BASE_URL}/users?${queryString}`;

      console.log(`Fetching daily session stats: ${url}`);

      const response: AnalyticsApiResponse<DailySessionData> = await apiClient.get(url);
      setData(response.data);
    } catch (err) {
      console.error("Error fetching daily session stats:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

export function useUserDetailAnalytics(userId: string | null, filters: AnalyticsFilters) {
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {
        start_date: filters.startDate.toISOString(),
        end_date: filters.endDate.toISOString(),
      };

      if (filters.targetLanguage) params.target_language = filters.targetLanguage;

      const queryString = new URLSearchParams(params).toString();
      const url = `${ANALYTICS_BASE_URL}/users/${userId}?${queryString}`;

      console.log(`Fetching user detail: ${url}`);

      const response: AnalyticsApiResponse<UserDetailData> = await apiClient.get(url);
      setData(response.data);
    } catch (err) {
      console.error(`Error fetching user detail for ${userId}:`, err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [userId, filters]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Ads analytics types
export interface AdsOverviewData {
  interstitial: {
    shown: number;
    closed: number;
    failed: number;
    completion_rate: number;
    avg_duration_seconds: number;
    estimated_revenue: number;
  };
  rewarded: {
    shown: number;
    completed: number;
    closed_without_reward: number;
    failed: number;
    completion_rate: number;
    avg_duration_seconds: number;
    total_lives_earned: number;
    estimated_revenue: number;
  };
  total_ads_shown: number;
  total_estimated_revenue: number;
  by_platform: Array<{
    platform: string;
    interstitial_shown: number;
    rewarded_shown: number;
    rewarded_completed: number;
  }>;
}

export interface AdsUserMetrics {
  users: Array<{
    user_id: string;
    interstitial_shown: number;
    rewarded_shown: number;
    rewarded_completed: number;
    interstitial_failed: number;
    rewarded_failed: number;
    total_failed: number;
    lives_earned: number;
    total_ads: number;
    total_ad_time_minutes: number;
    estimated_revenue: number;
  }>;
  total_users_with_ads: number;
  avg_ads_per_user: number;
  avg_revenue_per_user: number;
}

export interface AdsDailyStat {
  date: string;
  interstitial_shown: number;
  rewarded_shown: number;
  rewarded_completed: number;
  total_ads_shown: number;
  unique_users: number;
  estimated_revenue: number;
}

export interface AdsDailyData {
  daily_stats: AdsDailyStat[];
  total_days: number;
}

export interface AdsSummary {
  total_ads_shown: number;
  total_interstitial: number;
  total_rewarded: number;
  total_rewarded_completed: number;
  total_lives_earned: number;
  unique_users: number;
  avg_ads_per_user: number;
  total_estimated_revenue: number;
  avg_revenue_per_user: number;
}

export interface AdsData {
  overview?: AdsOverviewData;
  users?: AdsUserMetrics;
  daily?: AdsDailyData;
  summary?: AdsSummary;
}

export function useAdsAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<AdsData>("ads", filters);
}

// Skips analytics types
export interface SkipSummary {
  total_skip_events: number;
  total_exercises_skipped: number;
  avg_exercises_per_skip: number;
  unique_users_skipping: number;
  unique_components_skipped: number;
  unique_sections_affected: number;
}

export interface ComponentSkip {
  component: string;
  skip_count: number;
  exercises_skipped: number;
  unique_users: number;
  avg_exercises_per_skip: number;
}

export interface ReasonSkip {
  reason: string;
  count: number;
  percentage: number;
  exercises_skipped: number;
}

export interface SectionSkip {
  section: string;
  skip_count: number;
  exercises_skipped: number;
  unique_components: number;
}

export interface WordSkip {
  word_id: string;
  word_target: string;
  word_base: string;
  skip_count: number;
  sections: string[];
  components: string[];
}

export interface UserSkip {
  user_id: string;
  skip_count: number;
  exercises_skipped: number;
  unique_components: number;
  unique_sections: number;
}

export interface SkipTimeSeriesPoint {
  date: string;
  skip_count: number;
  exercises_skipped: number;
  unique_users: number;
}

export interface SkipsData {
  summary: SkipSummary;
  by_component: ComponentSkip[];
  by_reason: ReasonSkip[];
  by_section: SectionSkip[];
  by_word: WordSkip[];
  by_user: UserSkip[];
  time_series: SkipTimeSeriesPoint[];
}

export function useSkipsAnalytics(filters: AnalyticsFilters) {
  return useAnalytics<SkipsData>("skips", filters);
}

// LLM Usage analytics hook
export function useLLMUsageAnalytics(filters: AnalyticsFilters) {
  const [data, setData] = useState<LLMUsageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Format dates for API (YYYY-MM-DD)
      const startDate = filters.startDate.toISOString().split("T")[0];
      const endDate = filters.endDate.toISOString().split("T")[0];

      // Fetch both top users and daily aggregated usage in parallel
      const [topUsersResponse, dailyUsageResponse] = await Promise.all([
        apiClient.get(
          `${mobileAppUri}/api/v1/admin/usage?action=top_users_tokens&start_date=${startDate}&end_date=${endDate}&limit=20`
        ),
        apiClient.get(
          `${mobileAppUri}/api/v1/admin/usage?action=daily_llm_usage&start_date=${startDate}&end_date=${endDate}`
        ),
      ]);

      console.log("LLM Usage - topUsersResponse:", topUsersResponse);
      console.log("LLM Usage - dailyUsageResponse:", dailyUsageResponse);

      // Parse the top users response
      let topUsersData;
      if (typeof topUsersResponse === "string") {
        topUsersData = JSON.parse(topUsersResponse);
      } else if (typeof topUsersResponse.body === "string") {
        topUsersData = JSON.parse(topUsersResponse.body);
      } else if (topUsersResponse.body) {
        topUsersData = topUsersResponse.body;
      } else {
        topUsersData = topUsersResponse;
      }

      // Parse the daily usage response
      let dailyUsageData;
      if (typeof dailyUsageResponse === "string") {
        dailyUsageData = JSON.parse(dailyUsageResponse);
      } else if (typeof dailyUsageResponse.body === "string") {
        dailyUsageData = JSON.parse(dailyUsageResponse.body);
      } else if (dailyUsageResponse.body) {
        dailyUsageData = dailyUsageResponse.body;
      } else {
        dailyUsageData = dailyUsageResponse;
      }

      console.log("LLM Usage - parsed topUsersData:", topUsersData);
      console.log("LLM Usage - parsed dailyUsageData:", dailyUsageData);

      // Map top users
      const users: LLMUserUsage[] = (topUsersData.data || []).map((user: any) => ({
        user_id: user.user_id,
        total_tokens: user.total_tokens || 0,
        total_cost: user.total_cost || 0,
        request_count: user.request_count || 0,
        avg_tokens_per_request: user.avg_tokens_per_request || 0,
      }));

      // Map daily stats for chart
      const dailyStats: LLMDailyStat[] = (dailyUsageData.daily_stats || []).map((day: any) => ({
        date: day.date,
        total_tokens: day.total_tokens || 0,
        input_tokens: 0,
        output_tokens: 0,
        total_cost: day.total_cost || 0,
        total_requests: day.total_requests || 0,
        unique_users: day.unique_users || 0,
        usage_by_model: {},
        cost_by_model: day.cost_by_model || {},
      }));

      // Use totals from daily aggregation (more accurate across all users)
      const totals = dailyUsageData.totals || {};
      const totalTokens = totals.total_tokens || users.reduce((sum, u) => sum + u.total_tokens, 0);
      const totalCost = totals.total_cost || users.reduce((sum, u) => sum + u.total_cost, 0);
      const totalRequests =
        totals.total_requests || users.reduce((sum, u) => sum + u.request_count, 0);
      const uniqueUsers = totals.unique_users || users.length;

      // Per-model and per-feature (endpoint) breakdowns from the daily usage response
      const usageByModel: Record<string, LLMModelUsage> = dailyUsageData.usage_by_model || {};
      const usageByEndpoint: Record<string, LLMEndpointUsage> =
        dailyUsageData.usage_by_endpoint || {};

      // Build summary
      const dailyTokenLimit = 50000;
      const summary = {
        total_tokens_used: totalTokens,
        total_cost: totalCost,
        total_requests: totalRequests,
        unique_users: uniqueUsers,
        avg_tokens_per_request: totalRequests > 0 ? Math.round(totalTokens / totalRequests) : 0,
        avg_cost_per_request: totalRequests > 0 ? totalCost / totalRequests : 0,
        token_limit: dailyTokenLimit,
        remaining_tokens: Math.max(0, dailyTokenLimit - totalTokens),
        usage_percentage: Math.round((totalTokens / dailyTokenLimit) * 100),
      };

      setData({
        summary,
        usage_by_model: usageByModel,
        usage_by_endpoint: usageByEndpoint,
        top_users: users,
        daily_stats: dailyStats,
      });
    } catch (err) {
      console.error("Error fetching LLM usage analytics:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refetch = useCallback(() => {
    return fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
}

// Clear cache function
export function clearAnalyticsCache() {
  Object.keys(cache).forEach((key) => delete cache[key]);
  console.log("Analytics cache cleared");
}

// Export for manual API calls if needed
export const analyticsAPI = {
  overview: (filters: AnalyticsFilters) => {
    const params = new URLSearchParams({
      start_date: filters.startDate.toISOString(),
      end_date: filters.endDate.toISOString(),
      ...(filters.targetLanguage && { target_language: filters.targetLanguage }),
      ...(filters.baseLanguage && { base_language: filters.baseLanguage }),
    });
    return apiClient.get<AnalyticsApiResponse<OverviewData>>(
      `${ANALYTICS_BASE_URL}/overview?${params}`
    );
  },

  engagement: (filters: AnalyticsFilters) => {
    const params = new URLSearchParams({
      start_date: filters.startDate.toISOString(),
      end_date: filters.endDate.toISOString(),
      ...(filters.granularity && { granularity: filters.granularity }),
      ...(filters.metricType && { metric_type: filters.metricType }),
      ...(filters.targetLanguage && { target_language: filters.targetLanguage }),
    });
    return apiClient.get<AnalyticsApiResponse<EngagementData>>(
      `${ANALYTICS_BASE_URL}/engagement?${params}`
    );
  },

  learning: (filters: AnalyticsFilters) => {
    const params = new URLSearchParams({
      start_date: filters.startDate.toISOString(),
      end_date: filters.endDate.toISOString(),
      ...(filters.metricType && { metric_type: filters.metricType }),
      ...(filters.targetLanguage && { target_language: filters.targetLanguage }),
      ...(filters.section && { section: filters.section }),
      ...(filters.component && { component: filters.component }),
    });
    return apiClient.get<AnalyticsApiResponse<LearningData>>(
      `${ANALYTICS_BASE_URL}/learning?${params}`
    );
  },

  content: (filters: AnalyticsFilters) => {
    const params = new URLSearchParams({
      start_date: filters.startDate.toISOString(),
      end_date: filters.endDate.toISOString(),
      ...(filters.metricType && { metric_type: filters.metricType }),
      ...(filters.targetLanguage && { target_language: filters.targetLanguage }),
      ...(filters.section && { section: filters.section }),
    });
    return apiClient.get<AnalyticsApiResponse<ContentData>>(
      `${ANALYTICS_BASE_URL}/content?${params}`
    );
  },

  notifications: (filters: AnalyticsFilters) => {
    const params = new URLSearchParams({
      start_date: filters.startDate.toISOString(),
      end_date: filters.endDate.toISOString(),
      ...(filters.metricType && { metric_type: filters.metricType }),
      ...(filters.deviceId && { device_id: filters.deviceId }),
    });
    return apiClient.get<AnalyticsApiResponse<NotificationData>>(
      `${ANALYTICS_BASE_URL}/notifications?${params}`
    );
  },

  topUsers: (filters: AnalyticsFilters, limit: number = 50) => {
    const params = new URLSearchParams({
      start_date: filters.startDate.toISOString(),
      end_date: filters.endDate.toISOString(),
      limit: limit.toString(),
      // No mode - defaults to 'login' for efficient queries
      ...(filters.targetLanguage && { target_language: filters.targetLanguage }),
    });
    return apiClient.get<AnalyticsApiResponse<TopUsersData>>(
      `${ANALYTICS_BASE_URL}/users?${params}`
    );
  },

  userDetail: (userId: string, filters: AnalyticsFilters) => {
    const params = new URLSearchParams({
      start_date: filters.startDate.toISOString(),
      end_date: filters.endDate.toISOString(),
      ...(filters.targetLanguage && { target_language: filters.targetLanguage }),
    });
    return apiClient.get<AnalyticsApiResponse<UserDetailData>>(
      `${ANALYTICS_BASE_URL}/users/${userId}?${params}`
    );
  },
};
